import GeoLookupProvider from '@/contexts/geoLookup';
import { ModalsContextProvider } from '@/contexts/modals';
import reddishFavicon from '@/static/assets/favicon-reddish.ico';
import pinkishFavicon from '@/static/assets/favicon.ico';
import { useLangAndRegion } from '@/store/useLangAndRegion';
import { useTheme } from '@/store/useTheme';
import '@/styles/globals.css';
import { initializeDatadogRum } from '@/utils/datadogRum';
import { getLangAndRegion } from '@/utils/getLocaleStatically';
import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider } from '@lemonade-hq/nuis';
import { LemonadeApp, useComplianceProvider } from '@lemonade-hq/nuiza';
import { GoogleTagManager } from '@next/third-parties/google';
import { ClickToComponent } from 'click-to-react-component';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import {
  analyticsConfig,
  authConfig,
  errorConfig,
  infraConfig,
  layoutConfig,
  localeConfig,
  monitoringConfig,
  navConfig,
  securityConfig,
  userConfig,
} from '../config';

initializeDatadogRum();

const favicons = {
  pinkish: pinkishFavicon.src,
  reddish: reddishFavicon.src,
} as const;

function DatadogRumTrackingByConsent() {
  const { cookieTypesEnabledList } = useComplianceProvider();

  useEffect(() => {
    if (cookieTypesEnabledList.includes('performance')) {
      datadogRum.setTrackingConsent('granted');
    }
  }, [cookieTypesEnabledList]);

  return null;
}

export default function App({ Component, pageProps }: AppProps) {
  const { asPath } = useRouter();
  // we need to set theme name as global state to set it form LPG pages
  const storeTheme = useTheme(state => state.theme);
  const [storeLang, storeRegion] = useLangAndRegion(state => [state.lang, state.region]);

  const [urlLang, urlRegion] = getLangAndRegion(asPath);

  const isLpgPage = useMemo(() => asPath.startsWith('/lp'), [asPath]);
  const urlTheme = useMemo(() => (asPath.startsWith('/de') ? 'reddish' : 'pinkish'), [asPath]);
  const isEmbeddedPage = useMemo(() => asPath.includes('embed=true'), [asPath]);

  const theme = useMemo(() => {
    return isLpgPage ? storeTheme : urlTheme;
  }, [urlTheme, storeTheme, isLpgPage]);

  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  const loadGtm = process.env.NEXT_PUBLIC_ENABLE_GTM === 'true' && gtmId;

  return (
    <>
      {loadGtm && (
        <GoogleTagManager
          gtmId={gtmId}
          dataLayer={{ country: storeRegion ?? urlRegion }} // initialize dataLayer like we do in Monolu
          gtmScriptUrl="https://www.lemonade.com/dtoc/"
        />
      )}
      <ClickToComponent editor="cursor" />
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="Referrer" content="origin" />
        <meta name="google" content="notranslate" />
        <meta name="screen-orientation" content="portrait" />
        <meta name="orientation" content="portrait" />
        {process.env.DISALLOW_ROBOTS && <meta key="robots" name="robots" content="noindex, nofollow" />}
        {theme && <link rel="icon" type="image/x-icon" href={favicons[theme]} />}
      </Head>

      <ThemeProvider themeName={theme}>
        <LemonadeApp
          analytics={analyticsConfig()}
          auth={authConfig()}
          error={errorConfig().hasError}
          infra={infraConfig()}
          layout={layoutConfig(isEmbeddedPage)}
          locale={{
            ...localeConfig(),
            lang: storeLang ?? urlLang,
            region: storeRegion ?? urlRegion,
          }}
          monitoring={monitoringConfig()}
          nav={navConfig()}
          security={securityConfig()}
          user={userConfig()}
          ssr={true} // important to generate pages in build time
        >
          <DatadogRumTrackingByConsent />
          <ModalsContextProvider>
            <GeoLookupProvider>
              <Component {...pageProps} />
            </GeoLookupProvider>
          </ModalsContextProvider>
        </LemonadeApp>
      </ThemeProvider>
    </>
  );
}
